import { lookupScaledScore, matchesTagFilters } from 'my-core/quiz-scoring-utils';
import { getMultiPartStatus, getStatus } from 'my-core/quiz-utils';
import { mergeSumObjects } from 'my-utils';

const ALL_SECTIONS_KEY = '__all__';

export default function buildQuizScoreResults(
  attemptTimestamp,
  quizElements,
  computedScoresConfig,
  submissions,
  customTagsSlug,
) {
  const results = {
    total: 0,
    points: 0,
    sectionsBreakdown: {},
    computedScores: {},
    countByStatus: {},
    actionStats: { [ALL_SECTIONS_KEY]: emptyActionStats() },
    sectionsQuestions: [[]],
  };
  const submissionByQuestionId = Object.fromEntries(
    submissions?.map(s => [s.quiz_question_id, s.submission_attempts?.[attemptTimestamp]]) || [],
  );
  let currentSection = null;
  let sectionPos = 0;
  let questionIdx = -1;
  let sawAnyQuestion = false;
  let currentSectionQuestions = results.sectionsQuestions[0];

  const pushQuestionRow = row => {
    sectionPos++;
    questionIdx++;

    currentSectionQuestions.push({
      ...row,
      position: `${results.sectionsQuestions.length}.${sectionPos}`,
      sectionPosition: sectionPos,
      sectionName: currentSection?.name || null,
      questionIdx,
    });
  };

  quizElements.forEach((el, elementIdx) => {
    if (el.type === 'SectionBreak') {
      if (sawAnyQuestion) {
        currentSectionQuestions = [];
        results.sectionsQuestions.push(currentSectionQuestions);
      }
      currentSection = el;
      sectionPos = 0;
      return;
    }
    const question = el.question;
    if (!question) return;
    sawAnyQuestion = true;
    const activeSubmission = submissionByQuestionId[question.id];

    if (question.type === 'MultiPartQuestion') {
      question.sub_questions.forEach((sq, partIdx) => {
        const status = getMultiPartStatus(activeSubmission, sq, true);
        addQuestionToResults(
          results,
          sq,
          currentSection,
          activeSubmission?.result?.breakdown?.[sq.id],
          el.weight / question.sub_questions.length,
          status,
        );
        const subMeta = activeSubmission?.meta?.[sq.id];
        const flagged = !!activeSubmission?.sub_flagged?.includes(sq.id);
        if (activeSubmission) {
          accumulateActionStats(results.actionStats, currentSection, sq, status, {
            hasFlagged: flagged,
            hasChanged: (subMeta?.history?.length || 0) > 1,
            hasCrossedOut: hasCrossedOutOpts(subMeta, sq),
          });
        }
        pushQuestionRow({
          customTags: sq.custom_tags?.[customTagsSlug],
          flagged,
          idx: elementIdx,
          response: activeSubmission?.response?.[sq.id],
          partIdx,
          question,
          status,
          subQuestion: sq,
        });
      });
    } else {
      const status = getStatus(activeSubmission, question, true);
      addQuestionToResults(results, question, currentSection, activeSubmission?.result, el.weight, status);
      if (activeSubmission) {
        accumulateActionStats(results.actionStats, currentSection, question, status, {
          hasFlagged: !!activeSubmission.flagged,
          hasChanged: (activeSubmission.history?.length || 0) > 1,
          hasCrossedOut: hasCrossedOutOpts(activeSubmission, question),
        });
      }
      pushQuestionRow({
        customTags: question.custom_tags?.[customTagsSlug],
        flagged: !!activeSubmission?.flagged,
        idx: elementIdx,
        response: activeSubmission?.response,
        partIdx: 0,
        question,
        status,
        subQuestion: null,
      });
    }
  });

  Object.values(results.computedScores).forEach(cs => {
    cs.score = lookupScaledScore(cs.points, cs.matrix);
  });

  if (computedScoresConfig?.length) {
    for (const cs of computedScoresConfig) {
      const sectionScaleScores =
        currentSection ?
          cs.scale_names.map(s => results.computedScores[s])
        : [
            {
              total: results.total,
              points: results.points,
              score: results.points,
              min_score: 0,
              max_score: results.total,
              countByStatus: results.countByStatus,
            },
          ];
      const totalTotal = sectionScaleScores.reduce((sum, ss) => sum + ss.total, 0);
      const pointsTotal = sectionScaleScores.reduce((sum, ss) => sum + ss.points, 0);
      const scoreTotal = sectionScaleScores.reduce((sum, ss) => sum + ss.score, 0);
      const aggregatedCountByStatus = sectionScaleScores.reduce((agg, ss) => {
        Object.entries(ss.countByStatus || {}).forEach(([key, val]) => {
          agg[key] = (agg[key] || 0) + val;
        });
        return agg;
      }, {});
      results.actionStats[`scale__${cs.name}`] =
        currentSection ?
          cs.scale_names
            .map(n => results.actionStats[`scale__${n}`])
            .filter(Boolean)
            .reduce(mergeSumObjects)
        : results.actionStats[ALL_SECTIONS_KEY];
      const computedScore = {
        name: cs.name,
        points: pointsTotal,
        total: totalTotal,
        countByStatus: aggregatedCountByStatus,
      };
      switch (cs.type) {
        case 'sum_scaled': {
          results.computedScores[cs.name] = {
            ...computedScore,
            score: scoreTotal,
            min_score: sectionScaleScores.reduce((sum, ss) => sum + ss.min_score, 0),
            max_score: sectionScaleScores.reduce((sum, ss) => sum + ss.max_score, 0),
          };
          break;
        }
        case 'average_scaled': {
          results.computedScores[cs.name] = {
            ...computedScore,
            score: Math.round(scoreTotal / sectionScaleScores.length),
            min_score: sectionScaleScores[0]?.min_score,
            max_score: sectionScaleScores[0]?.max_score,
          };
          break;
        }
        case 'combined_raw_matrix': {
          results.computedScores[cs.name] = {
            ...computedScore,
            matrix: cs.score_matrix,
            score: lookupScaledScore(pointsTotal, cs.score_matrix),
            min_score: cs.score_matrix?.[0]?.score,
            max_score: cs.score_matrix?.at(-1)?.score,
          };
          break;
        }
      }
    }
  }

  const computedScores = Object.values(results.computedScores);
  const sectionsBreakdown = Object.values(results.sectionsBreakdown).map(b => ({ ...b, score: b.points / b.total }));

  const sectionSelectOptions = [
    { value: ALL_SECTIONS_KEY, label: 'All Sections' },
    ...(computedScores.length ?
      computedScores.map(row => ({ value: `scale__${row.name}`, label: row.name }))
    : sectionsBreakdown.map(row => ({ value: `sect__${row.name}`, label: row.name }))),
  ];

  return {
    ...results,
    sectionsBreakdown,
    computedScores,
    sectionSelectOptions,
  };
}

function emptyActionStats() {
  return {
    total: 0,
    flagged: { correct: 0, incorrect: 0, skipped: 0 },
    answer_changed: { correct: 0, incorrect: 0, skipped: 0 },
    crossed_out: { correct: 0, incorrect: 0, skipped: 0 },
  };
}

function addQuestionToResults(results, question, section, result, weight, status) {
  const points = weight * (result?.total ? result.points / result.total : 0);
  results.total += weight;
  results.points += points;
  results.countByStatus[status] = (results.countByStatus[status] || 0) + 1;

  if (section) {
    const sectResult = (results.sectionsBreakdown[section.name] ||= {
      name: section.name,
      total: 0,
      points: 0,
      countByStatus: {},
    });
    sectResult.total += weight;
    sectResult.points += points;
    sectResult.countByStatus[status] = (sectResult.countByStatus[status] || 0) + 1;

    section.score_scales?.forEach(scale => {
      const sectionScoreScale = (results.computedScores[scale.name] ||= {
        matrix: scale.matrix,
        min_score: scale.matrix?.[0].score,
        max_score: scale.matrix?.at(-1).score,
        name: scale.name,
        total: 0,
        points: 0,
        countByStatus: {},
      });
      if (!matchesTagFilters(question, scale.tag_filter)) return;
      sectionScoreScale.total += weight;
      sectionScoreScale.points += points;
      sectionScoreScale.countByStatus[status] = (sectionScoreScale.countByStatus[status] || 0) + 1;
    });
  }
}

function accumulateActionStats(actionStats, section, question, status, { hasChanged, hasCrossedOut, hasFlagged }) {
  const targets = [actionStats[ALL_SECTIONS_KEY]];
  if (section) {
    targets.push((actionStats[`sect__${section.name}`] ||= emptyActionStats()));
    section.score_scales?.forEach(scale => {
      if (!matchesTagFilters(question, scale.tag_filter)) return;
      targets.push((actionStats[`scale__${scale.name}`] ||= emptyActionStats()));
    });
  }
  targets.forEach(t => t.total++);
  if (hasFlagged) targets.forEach(t => t.flagged[status]++);
  if (hasChanged) targets.forEach(t => t.answer_changed[status]++);
  if (hasCrossedOut) targets.forEach(t => t.crossed_out[status]++);
}

// Mirrors QuizResultsCalculator#has_crossed_out_opts? – a question counts as
// crossed-out if it has explicit crossed_out_opts OR any of its option's
// highlights include a line_through tool.
function hasCrossedOutOpts(sub, question) {
  if (!sub) return false;
  if (sub.crossed_out_opts?.length) return true;
  const highlights = sub.highlights;
  const options = question?.options;
  if (!highlights || !options?.length) return false;
  return options.some(o => {
    const optHighlights = highlights[o.id];
    if (!optHighlights) return false;
    return Object.values(optHighlights).some(
      blockHighlights => Array.isArray(blockHighlights) && blockHighlights.some(hl => hl?.tool === 'line_through'),
    );
  });
}
